import { useTraderTime } from '../contexts/TraderTimeContext';
import React, { useState, useEffect } from 'react';
import { ordersAPI, OrderResponse, traderAPI, WithdrawLimitStatus } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Pagination } from '../components/ui/pagination';
import { Edit, Loader2, RefreshCw, X, TrendingDown, AlertTriangle, CheckCircle } from 'lucide-react';
import { timeUtils, formatAmount as formatAmountUtil } from '../lib/utils';
import { CreateOrderForm } from '../components/orders/CreateOrderForm';
import { EditOrderDialog } from '../components/orders/EditOrderDialog';
import { Switch } from '../components/ui/switch';
import { TRADER_REQUISITES_REFRESH_INTERVAL_MS } from '../lib/polling';

const SimpleCountdown: React.FC<{ targetDate: string; onExpire?: () => void; className?: string }> = ({
    targetDate,
    onExpire,
    className = ''
}) => {
    const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0, total: 0 });

    useEffect(() => {
        const calculateTimeLeft = () => {
            const target = timeUtils.parseUTC(targetDate).getTime();
            const now = Date.now();
            const difference = target - now;

            if (difference <= 0) {
                return { hours: 0, minutes: 0, seconds: 0, total: 0 };
            }

            const hours = Math.floor(difference / (1000 * 60 * 60));
            const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((difference % (1000 * 60)) / 1000);

            return { hours, minutes, seconds, total: difference };
        };

        const timer = setInterval(() => {
            const newTimeLeft = calculateTimeLeft();
            setTimeLeft(newTimeLeft);

            if (newTimeLeft.total <= 0) {
                clearInterval(timer);
                onExpire?.();
            }
        }, 1000);

        setTimeLeft(calculateTimeLeft());

        return () => clearInterval(timer);
    }, [targetDate, onExpire]);

    if (timeLeft.total <= 0) {
        return <span className={className}>Готово</span>;
    }

    return (
        <span className={`font-mono font-bold ${className}`}>
            {timeLeft.hours > 0 && `${String(timeLeft.hours).padStart(2, '0')}:`}
            {String(timeLeft.minutes).padStart(2, '0')}:{String(timeLeft.seconds).padStart(2, '0')}
        </span>
    );
};

export const WithdrawRequisitesPage: React.FC = () => {
    const { formatDate } = useTraderTime();
    const [orders, setOrders] = useState<OrderResponse[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [pageSize] = useState(50);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [cancellingOrder, setCancellingOrder] = useState<string | null>(null);
    const [updatingPauseOrder, setUpdatingPauseOrder] = useState<string | null>(null);
    const [editingOrder, setEditingOrder] = useState<OrderResponse | null>(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [withdrawLimit, setWithdrawLimit] = useState<WithdrawLimitStatus | null>(null);

    const loadOrders = async (showLoading = true, page: number = currentPage) => {
        try {
            if (showLoading) {
                setIsLoading(true);
            }
            setError(null);
            const response = await ordersAPI.getMyOrders(page, pageSize);

            // Фильтруем только активные ордера на вывод
            const activeWithdrawOrders = response.orders.filter(
                order => order.status === 'active' && order.order_type === 'withdraw'
            );

            // Обновляем только если данные изменились
            setOrders(prevOrders => {
                const dataString = JSON.stringify(activeWithdrawOrders);
                const prevString = JSON.stringify(prevOrders);

                if (dataString === prevString) {
                    return prevOrders;
                }

                return activeWithdrawOrders;
            });

            setTotalPages(response.pages);
            setTotalItems(response.total);
            setCurrentPage(response.page);
        } catch (err: any) {
            setError('Не удалось загрузить реквизиты');
            console.error('Error loading orders:', err);
        } finally {
            if (showLoading) {
                setIsLoading(false);
            }
        }
    };

    const loadWithdrawLimit = async () => {
        try {
            const status = await traderAPI.getWithdrawLimitStatus();
            setWithdrawLimit(status);
        } catch (err) {
            console.error('Error loading withdraw limit:', err);
        }
    };

    useEffect(() => {
        loadOrders(true, 1); // С индикатором при изменении refreshTrigger
        loadWithdrawLimit();
    }, [refreshTrigger]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        loadOrders(true, page);
    };

    // Автоматическое фоновое обновление накопленных сумм по реквизитам
    useEffect(() => {
        const interval = setInterval(() => {
            loadOrders(false); // Фоновое обновление без индикатора
            loadWithdrawLimit(); // Обновляем статус лимита
        }, TRADER_REQUISITES_REFRESH_INTERVAL_MS);

        return () => clearInterval(interval);
    }, []);

    const handleCancelOrder = async (orderId: string) => {
        try {
            setCancellingOrder(orderId);
            await ordersAPI.cancelOrder(orderId);
            await loadOrders();
        } catch (err: any) {
            setError('Не удалось отменить ордер');
        } finally {
            setCancellingOrder(null);
        }
    };

    const handleTogglePause = async (order: OrderResponse) => {
        try {
            setUpdatingPauseOrder(order.id);
            await ordersAPI.updateOrder(order.id, { is_paused: !order.is_paused });
            await loadOrders(false);
        } catch (err: any) {
            setError('Не удалось обновить паузу');
        } finally {
            setUpdatingPauseOrder(null);
        }
    };

    const handleOrderCreated = () => {
        setRefreshTrigger(prev => prev + 1);
    };


    const formatAmount = (amount: number, currency?: string) => {
        return formatAmountUtil(amount, currency);
    };

    const getStatusBadge = (status: string) => {
        const statusConfig = {
            active: { label: 'Активный', variant: 'default' as const },
            completed: { label: 'Завершен', variant: 'secondary' as const },
            cancelled: { label: 'Отменен', variant: 'destructive' as const },
        };

        const config = statusConfig[status as keyof typeof statusConfig] || {
            label: status,
            variant: 'secondary' as const,
        };

        return <Badge variant={config.variant}>{config.label}</Badge>;
    };

    const renderOrderCard = (order: OrderResponse) => (
        <div
            key={order.id}
            className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${order.is_paused ? 'border-amber-200 dark:border-amber-800 bg-amber-50/40 dark:bg-amber-950/40' : 'border-gray-200 dark:border-slate-700'}`}
        >
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                        <TrendingDown className="h-4 w-4 text-red-600 dark:text-red-300" />
                        <span className="font-medium">Вывод</span>
                        {getStatusBadge(order.status)}
                        {order.is_paused && (
                            <Badge variant="secondary" className="bg-amber-100 dark:bg-amber-950 text-amber-800 dark:text-amber-200">
                                На паузе
                            </Badge>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                        <div>
                            <p className="text-gray-600 dark:text-slate-300">Объём ордера:</p>
                            <p className="font-medium">
                                {formatAmount(order.from_amount)} {order.from_currency}
                            </p>
                            <p className="text-gray-600 dark:text-slate-300">
                                Остаток: {formatAmount(order.remaining_fiat_amount ?? Math.max(0, order.from_amount - Math.max(order.reserved_amount || 0, order.accumulated_amount || 0)))} {order.from_currency}
                            </p>

                        </div>

                        <div>
                            <p className="text-gray-600 dark:text-slate-300">Создан:</p>
                            <p>{formatDate(order.created_at)}</p>
                        </div>

                        <div>
                            <p className="text-gray-600 dark:text-slate-300">ID:</p>
                            <p className="font-mono text-xs">{order.id}</p>
                        </div>

                        {/* Информация о выводе */}
                        <div className="md:col-span-2 lg:col-span-3">
                            <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-md p-3">
                                <p className="text-sm text-green-800 dark:text-green-200">
                                    💸 Ордер на вывод: вы переводите клиентам на их реквизиты
                                </p>
                            </div>
                        </div>

                        {/* Прогресс заполнения */}
                        {order.accumulated_amount !== undefined && (
                            <div className="md:col-span-2 lg:col-span-3">
                                <p className="text-gray-600 dark:text-slate-300">Прогресс заполнения:</p>
                                <div className="mt-2 space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600 dark:text-slate-300">
                                            Обработано: {formatAmount(order.accumulated_amount)} из {formatAmount(order.from_amount)} {order.from_currency}
                                        </span>
                                        <span className="font-medium">
                                            {formatAmountUtil((order.accumulated_amount / order.from_amount) * 100)}%
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
                                        <div
                                            className={`h-2 rounded-full transition-all duration-300 ${order.accumulated_amount >= order.from_amount
                                                ? 'bg-green-600'
                                                : 'bg-blue-600'
                                                }`}
                                            style={{ width: `${Math.min((order.accumulated_amount / order.from_amount) * 100, 100)}%` }}
                                        ></div>
                                    </div>
                                    {order.accumulated_amount >= order.from_amount && (
                                        <div className="text-green-600 dark:text-green-300 text-sm font-medium">
                                            ✓ Ордер полностью заполнен
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {order.status === 'active' && (
                    <div className="ml-4 flex flex-col items-end gap-2">
                        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-slate-300">
                            <span>{order.is_paused ? 'На паузе' : 'Прием заявок'}</span>
                            <Switch
                                checked={!order.is_paused}
                                onCheckedChange={() => handleTogglePause(order)}
                                disabled={updatingPauseOrder === order.id}
                            />
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingOrder(order)}
                        >
                            <Edit className="h-4 w-4" />
                            Редактировать
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCancelOrder(order.id)}
                            disabled={cancellingOrder === order.id}
                        >
                            {cancellingOrder === order.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <X className="h-4 w-4" />
                            )}
                            Отменить
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-slate-100">Реквизиты для вывода</h1>
                <p className="text-gray-600 dark:text-slate-300 mt-2">
                    Управляйте ордерами для отправки RUB клиентам
                </p>
            </div>

            {error && (
                <div className="p-4 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md">
                    {error}
                </div>
            )}

            {/* Статус лимита на withdraw */}
            {withdrawLimit && (
                <Card className={withdrawLimit.is_limited ? 'border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950' : 'border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-950'}>
                    <CardContent className="py-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                {withdrawLimit.is_limited ? (
                                    <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-300" />
                                ) : (
                                    <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-300" />
                                )}
                                <div>
                                    <p className={`font-medium ${withdrawLimit.is_limited ? 'text-amber-800 dark:text-amber-200' : 'text-green-800 dark:text-green-200'}`}>
                                        Лимит транзакций на вывод
                                    </p>
                                    <p className={`text-sm ${withdrawLimit.is_limited ? 'text-amber-600 dark:text-amber-300' : 'text-green-600 dark:text-green-300'}`}>
                                        Обработано {withdrawLimit.completed_count_2h} из {withdrawLimit.limit} за последние 2 часа
                                    </p>
                                </div>
                            </div>
                            <div className="text-right">
                                {withdrawLimit.is_limited ? (
                                    <div className="flex items-center gap-2">
                                        <div>
                                            <p className="text-xs text-amber-600 dark:text-amber-300">Разблокировка через:</p>
                                            {withdrawLimit.next_available_at && (
                                                <SimpleCountdown
                                                    targetDate={withdrawLimit.next_available_at}
                                                    onExpire={() => loadWithdrawLimit()}
                                                    className="text-amber-800 dark:text-amber-200"
                                                />
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <Badge className="bg-green-100 dark:bg-green-950 text-green-800 dark:text-green-200">
                                        Осталось: {withdrawLimit.remaining}
                                    </Badge>
                                )}
                            </div>
                        </div>
                        {withdrawLimit.is_limited && (
                            <p className="text-xs text-amber-600 dark:text-amber-300 mt-2">
                                Новые выплаты не назначаются, подтверждение следующих выплат недоступно до истечения таймера
                            </p>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Контент */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Форма создания ордера на вывод */}
                <CreateOrderForm orderType="withdraw" onOrderCreated={handleOrderCreated} />

                {/* Список активных реквизитов */}
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                        <div>
                            <CardTitle>Рабочие реквизиты</CardTitle>
                            <p className="text-sm text-gray-600 dark:text-slate-300 mt-1">
                                Активные ордера для вывода средств
                            </p>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => loadOrders(true)}
                            disabled={isLoading}
                        >
                            <RefreshCw className="h-4 w-4" />
                        </Button>
                    </CardHeader>
                    <CardContent>
                        {isLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="h-8 w-8 animate-spin" />
                                <span className="ml-2">Загрузка...</span>
                            </div>
                        ) : orders.length === 0 ? (
                            <div className="text-center py-8 text-gray-500 dark:text-slate-400">
                                <p>Нет активных реквизитов</p>
                                <p className="text-sm mt-1">
                                    Создайте ордер на вывод
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {orders.map(order => renderOrderCard(order))}

                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={handlePageChange}
                                    totalItems={totalItems}
                                    pageSize={pageSize}
                                />
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
            <EditOrderDialog
                order={editingOrder}
                open={Boolean(editingOrder)}
                onOpenChange={(open) => {
                    if (!open) setEditingOrder(null);
                }}
                onSaved={() => loadOrders(true)}
            />
        </div>
    );
};
